import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuestionsFeed, useSession, useCreateSession, useUpdateSession, useCompleteSession, useCreateShare } from '../lib/api';
import { SwipeCard } from '../components/SwipeCard';
import { markFlashcardReviewed, flagFlashcard, unflagFlashcard } from '../lib/flashcard-progress';
import { scrollAppToTop } from '../lib/scroll';

const FC_TUTORIAL_KEY = 'fc-tutorial-dismissed';

export function FlashCardsPage() {
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();

  const { data: feed } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: sessionData } = useSession(sessionId);
  const updateSession = useUpdateSession();
  const completeSession = useCompleteSession();

  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [gotItSet, setGotItSet] = useState<Set<string>>(new Set());
  const [wrongSet, setWrongSet] = useState<Set<string>>(new Set());
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const hydratedRef = useRef<string | null>(null);
  const [sessionQuestionIds, setSessionQuestionIds] = useState<string[] | null>(null);
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'copied'>('idle');
  const createShare = useCreateShare();

  // Show tutorial on first use
  useEffect(() => {
    if (!localStorage.getItem(FC_TUTORIAL_KEY)) setShowTutorial(true);
  }, []);

  const dismissTutorial = useCallback(() => {
    setShowTutorial(false);
    localStorage.setItem(FC_TUTORIAL_KEY, '1');
  }, []);

  // All flashcard questions for this section
  const flashcards = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'flashcard'),
    [feed?.questions],
  );

  // Hydrate session state from server
  useEffect(() => {
    if (!sessionData?.session) return;
    const s = sessionData.session;
    const key = `${s.id}:${s.currentIndex}:${s.correctCount}:${s.questionsAnswered}`;
    if (hydratedRef.current === key) return;
    hydratedRef.current = key;

    setSessionQuestionIds(s.questionIds);
    setIdx(s.currentIndex);

    // Rebuild gotIt/wrong sets from session answers
    if (s.answers) {
      const g = new Set<string>();
      const w = new Set<string>();
      for (const [qId, ans] of Object.entries(s.answers)) {
        if (ans.correct) g.add(qId);
        else w.add(qId);
      }
      setGotItSet(g);
      setWrongSet(w);
    }
  }, [sessionData]);

  // Order flashcards by session question IDs
  const orderedCards = useMemo(() => {
    if (!sessionQuestionIds) return flashcards;
    const byId = new Map(flashcards.map((q: any) => [q.id, q]));
    return sessionQuestionIds.map(id => byId.get(id)).filter(Boolean) as any[];
  }, [flashcards, sessionQuestionIds]);

  const currentCard = orderedCards[idx];
  const payload = currentCard?.payload as any;
  const totalCards = orderedCards.length;
  const gotItCount = gotItSet.size;
  const wrongCount = wrongSet.size;

  const handleShare = useCallback(async () => {
    if (!currentCard || shareStatus === 'sharing') return;
    setShareStatus('sharing');
    try {
      const res = await createShare.mutateAsync({ question_id: currentCard.id, share_type: 'social' });
      const url = `https://app.squishyschool.com/q/${res.share_id}`;
      if (navigator.share) {
        await navigator.share({ title: 'Squishy School Flash Card', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch {
      setShareStatus('idle');
    }
  }, [currentCard, shareStatus, createShare]);

  // Reset flip state when navigating
  useEffect(() => {
    setFlipped(false);
  }, [idx]);

  // Scroll to top on card change
  useEffect(() => {
    scrollAppToTop('smooth');
  }, [idx]);

  const persistProgress = useCallback((newIdx: number, correct: number, answered: number) => {
    if (!sessionId) return;
    updateSession.mutate({
      id: sessionId,
      currentIndex: newIdx,
      correctCount: correct,
      questionsAnswered: answered,
    });
  }, [sessionId, updateSession]);

  const handleSwipeRight = useCallback(() => {
    if (!currentCard) return;
    const newGotIt = new Set(gotItSet);
    newGotIt.add(currentCard.id);
    const newWrong = new Set(wrongSet);
    newWrong.delete(currentCard.id);
    setGotItSet(newGotIt);
    setWrongSet(newWrong);

    // Persist to localStorage
    markFlashcardReviewed(currentCard.id);
    unflagFlashcard(currentCard.id);

    const nextIdx = idx + 1;
    if (nextIdx >= totalCards) {
      setSessionComplete(true);
      if (sessionId) {
        completeSession.mutate(sessionId);
      }
    } else {
      setIdx(nextIdx);
      persistProgress(nextIdx, newGotIt.size, newGotIt.size + newWrong.size);
    }
  }, [currentCard, gotItSet, wrongSet, idx, totalCards, sessionId, completeSession, persistProgress]);

  const handleSwipeLeft = useCallback(() => {
    if (!currentCard) return;
    const newWrong = new Set(wrongSet);
    newWrong.add(currentCard.id);
    const newGotIt = new Set(gotItSet);
    newGotIt.delete(currentCard.id);
    setGotItSet(newGotIt);
    setWrongSet(newWrong);

    // Persist to localStorage
    markFlashcardReviewed(currentCard.id);
    flagFlashcard(currentCard.id);

    const nextIdx = idx + 1;
    if (nextIdx >= totalCards) {
      setSessionComplete(true);
      if (sessionId) {
        completeSession.mutate(sessionId);
      }
    } else {
      setIdx(nextIdx);
      persistProgress(nextIdx, newGotIt.size, newGotIt.size + newWrong.size);
    }
  }, [currentCard, gotItSet, wrongSet, idx, totalCards, sessionId, completeSession, persistProgress]);

  const goBack = () => {
    if (idx > 0) {
      const newIdx = idx - 1;
      setIdx(newIdx);
      persistProgress(newIdx, gotItSet.size, gotItSet.size + wrongSet.size);
    }
  };

  const goNext = () => {
    if (idx < totalCards - 1) {
      const newIdx = idx + 1;
      setIdx(newIdx);
      persistProgress(newIdx, gotItSet.size, gotItSet.size + wrongSet.size);
    }
  };

  // Session complete screen
  if (sessionComplete) {
    const accuracy = totalCards > 0 ? Math.round((gotItCount / totalCards) * 100) : 0;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center space-y-4">
          <div className="text-5xl">&#x1F389;</div>
          <h1 className="text-xl font-bold text-squishy-dark">Flashcards Complete!</h1>
          <div className="flex justify-center gap-6 text-sm">
            <div>
              <div className="text-2xl font-bold text-green-600">{gotItCount}</div>
              <div className="text-slate-500">Got it</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-500">{wrongCount}</div>
              <div className="text-slate-500">Review</div>
            </div>
          </div>
          <div className="text-3xl font-bold text-squishy-primary">{accuracy}%</div>
          <p className="text-xs text-slate-400">Cards you got right</p>
          <button
            onClick={() => navigate(`/class/${sectionId}`)}
            className="w-full bg-squishy-primary text-white py-3 rounded-xl font-semibold text-sm active:scale-[0.97] transition-transform"
          >
            Back to Class
          </button>
        </div>
      </div>
    );
  }

  if (!currentCard) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-slate-400 text-sm">Loading flashcards...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Tutorial overlay */}
      {showTutorial && <FlashCardTutorial onDismiss={dismissTutorial} />}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b px-4 py-2.5">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(`/class/${sectionId}`)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-squishy-dark">Flash Cards</div>
            <div className="text-[10px] text-slate-400">{idx + 1} of {totalCards}</div>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-green-600 font-semibold">{gotItCount} &#x2713;</span>
            <span className="text-amber-500 font-semibold">{wrongCount} &#x2717;</span>
            <button
              onClick={handleShare}
              disabled={shareStatus === 'sharing'}
              className="ml-1 p-1.5 rounded-full text-slate-400 hover:text-squishy-primary hover:bg-teal-50 transition-colors"
              title="Share this card"
            >
              {shareStatus === 'copied' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-lg mx-auto mt-1.5">
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-squishy-primary rounded-full transition-all duration-300"
              style={{ width: `${((idx + 1) / totalCards) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Card area */}
      <div className="max-w-lg mx-auto px-4 pt-6">
        <SwipeCard
          enabled={flipped}
          onSwipeRight={handleSwipeRight}
          onSwipeLeft={handleSwipeLeft}
        >
          <div
            onClick={() => !flipped && setFlipped(true)}
            className={`min-h-[320px] rounded-2xl border-2 shadow-sm transition-all ${
              flipped
                ? 'bg-white border-teal-200'
                : 'bg-gradient-to-br from-squishy-primary to-teal-600 border-transparent cursor-pointer active:scale-[0.99]'
            }`}
          >
            {!flipped ? (
              /* Front of card */
              <div className="flex flex-col items-center justify-center p-6 min-h-[320px]">
                {payload?.topic && (
                  <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-4">
                    {payload.topic}
                  </span>
                )}
                <p className="text-lg font-semibold text-white text-center leading-relaxed">
                  {payload?.front}
                </p>
                <div className="mt-6 flex items-center gap-1.5 text-white/50 text-xs">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Tap to reveal answer
                </div>
              </div>
            ) : (
              /* Back of card */
              <div className="p-6 min-h-[320px]">
                {payload?.topic && (
                  <span className="inline-block text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full uppercase tracking-wider mb-3">
                    {payload.topic}
                  </span>
                )}
                <p className="text-xs font-medium text-slate-400 mb-2">Q: {payload?.front}</p>
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-sm text-squishy-dark leading-relaxed whitespace-pre-line">
                    {payload?.back}
                  </p>
                </div>
                {payload?.category && (
                  <div className="mt-4 text-[10px] text-slate-400">
                    {payload.category}
                  </div>
                )}
              </div>
            )}
          </div>
        </SwipeCard>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-6 px-2">
          <button
            onClick={goBack}
            disabled={idx === 0}
            className="flex items-center gap-1 text-xs text-slate-400 font-medium px-3 py-2 rounded-full border border-slate-200 disabled:opacity-30 active:scale-95 transition-transform"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {!flipped && (
            <button
              onClick={() => setFlipped(true)}
              className="text-xs text-squishy-primary font-semibold px-4 py-2 rounded-full border border-teal-200 bg-teal-50 active:scale-95 transition-transform"
            >
              Show Answer
            </button>
          )}

          <button
            onClick={goNext}
            disabled={idx >= totalCards - 1}
            className="flex items-center gap-1 text-xs text-slate-400 font-medium px-3 py-2 rounded-full border border-slate-200 disabled:opacity-30 active:scale-95 transition-transform"
          >
            Skip
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Flashcard Tutorial ────────────────────────────────────────────────────────

function FlashCardTutorial({ onDismiss }: { onDismiss: () => void }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: 'Tap to Reveal',
      desc: 'Tap the card to flip it and see the answer.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      bg: 'bg-teal-50 border-teal-200',
    },
    {
      title: 'Swipe Right: Got It!',
      desc: 'If you knew the answer, swipe right or tap "Got it" to mark it correct.',
      icon: (
        <svg className="w-12 h-12 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      ),
      bg: 'bg-green-50 border-green-200',
    },
    {
      title: 'Swipe Left: Review',
      desc: 'Need more practice? Swipe left or tap "Review" to mark it for later.',
      icon: (
        <svg className="w-12 h-12 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      ),
      bg: 'bg-amber-50 border-amber-200',
    },
  ];

  const current = steps[step]!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onDismiss}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-6 pt-8 pb-4 ${current.bg} border-b flex flex-col items-center`}>
          {current.icon}
          <h3 className="text-lg font-bold text-squishy-dark mt-3">{current.title}</h3>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 text-center leading-relaxed">{current.desc}</p>
          <div className="flex justify-center gap-2 mt-5">
            {steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-squishy-primary' : 'bg-slate-200'}`} />
            ))}
          </div>
          <div className="mt-5 flex gap-3">
            {step < steps.length - 1 ? (
              <>
                <button onClick={onDismiss} className="flex-1 text-sm text-slate-400 py-2.5 rounded-xl border">Skip</button>
                <button
                  onClick={() => setStep(step + 1)}
                  className="flex-1 bg-squishy-primary text-white py-2.5 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
                >
                  Next
                </button>
              </>
            ) : (
              <button
                onClick={onDismiss}
                className="w-full bg-squishy-primary text-white py-2.5 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
              >
                Let's Go!
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
